import { ButtonHTMLAttributes, HTMLAttributes } from 'react';
import { foundationRadiusTokens, foundationSemantic, foundationSpacingTokens } from '../../foundation';
import { cx } from './utils';

type MenuItemVariant = 'default' | 'destructive';

interface BaseProps {
  variant?: MenuItemVariant;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
}

type ButtonMenuItemProps = BaseProps & ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'button';
};

type DivMenuItemProps = BaseProps & HTMLAttributes<HTMLDivElement> & {
  as: 'div';
};

type MenuItemProps = ButtonMenuItemProps | DivMenuItemProps;

const variantClasses: Record<MenuItemVariant, string> = {
  default: foundationSemantic.menu.itemDefault,
  destructive: foundationSemantic.menu.itemDestructive
};

const baseClasses = [
  'w-full text-sm text-left',
  foundationSpacingTokens.menuItem,
  foundationRadiusTokens.sm,
  'transition-colors',
  foundationSemantic.interactive.focusRing
].join(' ');

export default function MenuItem(props: MenuItemProps) {
  const {
    as = 'button',
    variant = 'default',
    selected = false,
    className,
    ...rest
  } = props;

  const combinedClassName = cx(
    baseClasses,
    variantClasses[variant],
    selected && foundationSemantic.menu.itemSelected,
    foundationSemantic.interactive.hoverSurface,
    className
  );

  if (as === 'div') {
    const divProps = rest as DivMenuItemProps;
    return (
      <div role="menuitem" className={combinedClassName} {...divProps} />
    );
  }

  const buttonProps = rest as ButtonMenuItemProps;
  return (
    <button
      type="button"
      role="menuitem"
      className={combinedClassName}
      {...buttonProps}
    />
  );
}

export type { MenuItemProps, MenuItemVariant };
